import { msalInstance, getActiveAccount, SCOPES } from "../auth/msalConfig.js";
import * as etagCache from "./etag-cache.js";

/** Thrown when MSAL has no account or silent refresh fails twice in a row.
 *  AuthGate catches this on a re-render and flips to login redirect. */
export class AuthExpired extends Error {
  constructor(message = "operator-console: MSAL session expired") {
    super(message);
    this.name = "AuthExpired";
  }
}

/** Thrown for non-401 4xx/5xx -- caller decides UI handling. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly bodyText: string,
  ) {
    super(`operator-console: HTTP ${status}`);
    this.name = "ApiError";
  }
}

async function acquireBearer(): Promise<string> {
  const account = getActiveAccount();
  if (!account) throw new AuthExpired("no active account");
  const result = await msalInstance.acquireTokenSilent({ account, scopes: SCOPES });
  if (!result?.accessToken) throw new AuthExpired("silent refresh produced no access token");
  return result.accessToken;
}

/**
 * The engine origin a request goes to.
 *
 * VITE_ENGINE_URL is baked at build time. In dev, Vite proxies /internal/v0
 * so window.location.origin (the dev server) is correct. In production the
 * SPA is on console.ardept.app (SWA) and the engine is on api.ardept.app --
 * different origins, so we MUST use the absolute engine URL when set.
 */
function resolveEngineUrl(input: string): string {
  const engineOrigin = (import.meta.env.VITE_ENGINE_URL as string | undefined) ?? window.location.origin;
  return input.startsWith("http") ? input : `${engineOrigin}${input}`;
}

/**
 * The ONE bearer loop every authenticated request rides (JSON and binary alike):
 *  1. Acquire bearer via MSAL silent refresh, send.
 *  2. On 401 -> ONE forced silent refresh, send again.
 *  3. On a second 401 -> throw AuthExpired (AuthGate flips to login).
 *
 * `send` builds the request for a given bearer; the caller owns every other
 * header and how the body is read. Returns the final response plus the bearer
 * it went out with (fetchWithAuth's 304 re-ask reuses it).
 */
async function sendWithBearer(
  send: (bearer: string) => Promise<Response>,
): Promise<{ res: Response; bearer: string }> {
  let bearer = await acquireBearer();
  let res = await send(bearer);

  // 401 -> ONE retry after silent refresh; on second 401 flip to login.
  if (res.status === 401) {
    // Force a refresh by clearing MSAL's cached access token --
    // acquireTokenSilent with forceRefresh: true.
    const account = getActiveAccount();
    if (!account) throw new AuthExpired("401 with no active account");
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: SCOPES,
      forceRefresh: true,
    });
    if (!result?.accessToken) throw new AuthExpired("forced refresh failed");
    bearer = result.accessToken;
    res = await send(bearer);
    if (res.status === 401) {
      throw new AuthExpired("second 401 after forced refresh");
    }
  }

  return { res, bearer };
}

/**
 * Phase 15 D-FETCH-01 -- central fetcher.
 *  1. Acquire bearer via MSAL silent refresh.
 *  2. Inject Authorization: Bearer + If-None-Match (if known ETag).
 *  3. Handle 304 -> return cached body.
 *  4. Handle 401 -> ONE silent re-auth attempt -> on second 401 throw AuthExpired.
 *  5. Handle 2xx -> store ETag + body in cache, return parsed JSON.
 *  6. Handle other 4xx/5xx -> throw ApiError(status, bodyText).
 *
 * T-15-03-02 token-redact discipline: the Authorization header value is never
 * logged or surfaced in error messages. ApiError only carries status + bodyText.
 */
export async function fetchWithAuth<T>(
  input: string,
  init: RequestInit = {},
): Promise<T> {
  const url = resolveEngineUrl(input);
  const cacheKey = input;            // path+querystring is the cache key
  const knownEtag = etagCache.getEtag(cacheKey);

  const doFetch = async (bearer: string): Promise<Response> => {
    const headers = new Headers(init.headers as HeadersInit | undefined);
    headers.set("Authorization", `Bearer ${bearer}`);
    if (knownEtag) headers.set("If-None-Match", knownEtag);
    if (init.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    return fetch(url, { ...init, headers });
  };

  const { res, bearer } = await sendWithBearer(doFetch);

  if (res.status === 304) {
    const cached = etagCache.getBody<T>(cacheKey);
    if (cached !== undefined) return cached;
    // Cache miss on 304 -- engine returned 304 but we don't have the body.
    // Fall through and refetch without If-None-Match.
    const headers = new Headers(init.headers as HeadersInit | undefined);
    headers.set("Authorization", `Bearer ${bearer}`);
    const refresh = await fetch(url, { ...init, headers });
    if (!refresh.ok) throw new ApiError(refresh.status, await refresh.text());
    const body = (await refresh.json()) as T;
    const newEtag = refresh.headers.get("ETag");
    if (newEtag) etagCache.store(cacheKey, newEtag, body);
    return body;
  }

  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text);
  }

  // 2xx -- store ETag + body, return parsed.
  const body = (await res.json()) as T;
  const newEtag = res.headers.get("ETag");
  if (newEtag) etagCache.store(cacheKey, newEtag, body);
  return body;
}

/**
 * #1969 -- the binary sibling of fetchWithAuth: same engine origin, same bearer,
 * same single forced refresh on 401 (sendWithBearer), but the body is read as a
 * Blob and NEVER as JSON. Built for the staff document page, which shows and
 * downloads a rendered PDF (`GET /internal/v0/documents/{id}/pdf`): a clicked
 * link sends no Authorization header, so the console fetches the bytes with the
 * staff bearer and hands the browser an object URL instead.
 *
 * No ETag memo: the memo holds parsed bodies for polled JSON reads, and a
 * rendered document is immutable and read once per visit. No If-None-Match is
 * sent, so the engine cannot answer 304.
 *
 * A non-2xx is an ApiError(status, bodyText), exactly as fetchWithAuth throws
 * it, so a screen tells 404 (masked not-found) from 403 (role) the same way.
 */
export async function fetchBlobWithAuth(
  input: string,
  init: RequestInit = {},
): Promise<Blob> {
  const url = resolveEngineUrl(input);

  const doFetch = async (bearer: string): Promise<Response> => {
    const headers = new Headers(init.headers as HeadersInit | undefined);
    headers.set("Authorization", `Bearer ${bearer}`);
    return fetch(url, { ...init, headers });
  };

  const { res } = await sendWithBearer(doFetch);

  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text);
  }

  return res.blob();
}
